<template>
    <div v-if="displayProfileCTA" class="profile-cta">
        <AnswerTimeIndicator
            v-if="profile?.conversationStatistics.averageAnswerTimeCode"
            :average-answer-time-code="profile?.conversationStatistics.averageAnswerTimeCode"
        />
        <ClientOnly v-if="currentIdentityIsClient">
            <ProjectProposalProductTour />
            <!-- Empty fallback to prevent content layout shift -->
            <template #fallback></template>
        </ClientOnly>
        <VJoyButton
            v-if="!comesFromSearch && !shortlist"
            class="profile-cta-project"
            variant="main"
            size="small"
            full
            data-testid="contact-button-project-proposal"
            data-client-onboarding-propose-a-project
            :disabled="isCtaDisabled"
            @click="redirectToProjectProposal"
        >
            {{ contactCtaLabel }}
        </VJoyButton>
        <VJoyButton
            v-else-if="hasShortlistItems && !profileInShortlist"
            class="profile-cta-project"
            variant="main"
            size="small"
            :loading="loading"
            full
            data-testid="contact-button-in-shortlist"
            :disabled="isCtaDisabled"
            @click="handleAddToShortlist"
        >
            {{ t('profile.shortlist.add.button') }}
        </VJoyButton>
        <VJoyButton
            v-else-if="hasShortlistItems && profileInShortlist"
            class="profile-cta-project"
            variant="main"
            size="small"
            :loading="loading"
            full
            data-testid="contact-button-remove-shortlist"
            :disabled="isCtaDisabled"
            @click="handleRemoveFromShortlist"
        >
            {{ t('profile.shortlist.remove.button') }}
        </VJoyButton>
        <div v-else>
            <ExternalUserActionShortlistModeSelection />
            <VJoyButton
                class="profile-cta-project"
                variant="main"
                size="small"
                full
                data-testid="contact-button-shortlist"
                data-client-onboarding-propose-a-project
                :disabled="isCtaDisabled"
                @click="displayChoice"
            >
                {{ contactCtaLabel }}
            </VJoyButton>
        </div>
        <InstantConversationDialog :search-event-id="searchEventId" />
    </div>
</template>

<script setup lang="ts">
    import {useRoute} from '@typed-router';
    import {storeToRefs} from 'pinia';
    import {computed, ref, useAuth, useDataTracking, useHydrationState, useTranslation} from '#imports';
    import {useABTest, useFeatureFlag} from '@malt/feature-flags';
    import {safelyNavigateTo} from '@malt/utils';
    import {VJoyButton} from '@maltjoy/core-vue';
    import ExternalUserActionShortlistModeSelection from '~/components/external-user-action/ExternalUserActionShortlistModeSelection.vue';
    import {useComesFromAdvancedSearch} from '~/composables/useComesFromAdvancedSearch';
    import {eventBus} from '~/config/events';
    import {useInstantConversationStore} from '~/store/instantConversation.store';
    import {useProfileStore} from '~/store/profileStore';
    import AnswerTimeIndicator from './indicators/AnswerTimeIndicator.vue';
    import InstantConversationDialog from './InstantConversationDialog.vue';
    import ProjectProposalProductTour from './ProjectProposalProductTour.vue';
    import {useShortlistStore} from '@/store/shortlist/ShortlistStore';

    // Composables
    const {t} = useTranslation();
    const route = useRoute();
    const {isHydrated} = useHydrationState();

    // Stores
    const {currentIdentityIsClient, loggedIn} = storeToRefs(useAuth());
    const shortlistStore = useShortlistStore();
    const {profile} = storeToRefs(useProfileStore());
    const {shortlist, hasShortlistItems, profileInShortlist, comesFromSearch} = storeToRefs(shortlistStore);
    const {instantConversationUsage, instantConversationLimitReached} = storeToRefs(useInstantConversationStore());
    const isClientInstantConversationEnabled = useFeatureFlag('client-instant-conversation');
    const isLimitInstantConversationPerDayEnabled = useFeatureFlag('limit-number-of-instant-conversation-per-day');
    const {getVariationWithoutTracking} = useABTest();

    // Refs
    const loading = ref(false);

    // Computed values
    const isInOverviewMode = computed<boolean>(() => route?.fullPath?.includes('overview') || false);
    const isCtaDisabled = computed(() => isInOverviewMode.value || !isHydrated.value);
    const comesFromAdvancedSearch = useComesFromAdvancedSearch();
    const searchEventId = computed<string | undefined>(() => route?.query?.searchid as string);
    const displayProfileCTA = computed(() => !profile.value?.editMode && !comesFromAdvancedSearch.value);
    const contactCtaLabel = t('show.contactme.button.label');
    const displayInstantConversationUsage = computed(
        () =>
            isClientInstantConversationEnabled.value &&
            isLimitInstantConversationPerDayEnabled.value &&
            loggedIn.value &&
            currentIdentityIsClient.value &&
            getVariationWithoutTracking('instant-conversation-v2') === 'treatment',
    );

    async function handleAddToShortlist() {
        loading.value = true;
        const {track} = useDataTracking();
        const route = useRoute();
        const searchId = route?.query?.searchid as string;
        const sourceComponent = route?.query?.sourceComponent as string;

        const redirectUrl = await shortlistStore.add();

        if (redirectUrl) {
            track('freelancer_shortlisted', {
                search_event_id: searchId,
                freelancer_account_id: profile.value!.accountId,
            });
            location.href = `${redirectUrl}${sourceComponent ? `&sourceComponent=${sourceComponent}` : ''}`;
        }
        loading.value = false;
    }

    async function handleRemoveFromShortlist() {
        loading.value = true;
        await shortlistStore.remove();
        loading.value = false;
    }

    function displayChoice() {
        eventBus.emit('shortlist:display-choice');
    }

    function redirectToProjectProposal() {
        const {track} = useDataTracking();

        track('profile_action_cta_clicked', {
            freelancer_id: profile.value!.profileId,
            action_type: 'start_a_brief',
            search_id: searchEventId.value,
        });

        return safelyNavigateTo(
            {
                path: '/client/briefs/proposal',
                query: {
                    searchId: route?.query?.searchid,
                    profileViewId: profile.value!.profileViewId,
                    profileIds: profile.value!.profileId,
                    sourceComponent: route?.query?.sourceComponent,
                    entryPoint: 'profile_page',
                },
            },
            {externalToThisApp: true},
        );
    }
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .profile-cta {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: var(--joy-core-spacing-4);
        width: 100%;
    }
</style>
